import React, { useState, useRef } from 'react';
import { X, Linkedin, Facebook, Youtube, Instagram } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import ReCAPTCHA from 'react-google-recaptcha';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [captchaValue, setCaptchaValue] = useState(null);
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  const { theme } = useTheme();

  // Contact information (preserved for future use)
  const contactInfo = {
    email: 'info@sharedoxygen.com',
    phone: '+1 (555) 123-4567',
    address: '1234 Technology Drive, San Francisco, CA 94107',
    hours: 'Mon-Fri 9:00 AM - 6:00 PM PST'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCaptchaChange = async (value) => {
    setCaptchaValue(value);
    // After reCAPTCHA completes, automatically submit the form
    if (value) {
      await submitForm(value);
    }
  };

  const executeRecaptcha = () => {
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.execute();
      } catch (error) {
        console.error('reCAPTCHA execution error:', error);
        setFormStatus({
          submitted: true,
          success: false,
          message: 'Security verification failed. Please refresh the page and try again.'
        });
        setLoading(false);
      }
    } else {
      console.error('reCAPTCHA ref not available');
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Security verification not loaded. Please refresh the page and try again.'
      });
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
    });
    setCaptchaValue(null);
    // Delay reCAPTCHA reset to avoid DOM timing issues
    setTimeout(() => {
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.reset();
        } catch (error) {
          console.warn('reCAPTCHA reset warning:', error);
        }
      }
    }, 100);
  };

  const submitForm = async (recaptchaToken) => {
    try {
      // Prepare form data
      const submitData = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        'g-recaptcha-response': recaptchaToken
      };

      // Determine the endpoint
      const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const endpoint = isDevelopment ? 'http://localhost:8000/contact-handler.php' : '/contact-handler.php';

      console.log(`Submitting to: ${endpoint}`);

      // In development, try PHP backend first, fall back to simulation
      if (isDevelopment) {
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(submitData)
          });

          const result = await response.json();

          if (response.ok && result.success) {
            setFormStatus({ 
              submitted: true, 
              success: true, 
              message: result.message || 'Thank you! Your message has been sent.' 
            });
            resetForm();
            return;
          }
        } catch (phpError) {
          // PHP server not available, simulate success for development
          console.log('PHP server not available, simulating form submission');
          console.log('Form data that would be sent:', submitData);
          await new Promise(resolve => setTimeout(resolve, 800));
          setFormStatus({ 
            submitted: true, 
            success: true, 
            message: 'Thank you! Your message has been sent. (Development Mode - PHP server not running)' 
          });
          resetForm();
          return;
        }
      }

      // Production mode - use real PHP backend with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submitData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const result = await response.json();

      if (response.ok && result.success) {
        setFormStatus({ 
          submitted: true, 
          success: true, 
          message: result.message || 'Thank you! Your message has been sent.' 
        });
        resetForm();
      } else {
        console.error('Form submission failed:', response.status, result);
        setFormStatus({
          submitted: true,
          success: false,
          message: result.message || `There was an error submitting the form. Please try again or contact support.`
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      if (error.name === 'AbortError') {
        setFormStatus({ 
          submitted: true, 
          success: false, 
          message: 'Request timeout. The server took too long to respond. Please try again or email us directly at developer@sharedoxygen.com' 
        });
      } else {
        setFormStatus({ 
          submitted: true, 
          success: false, 
          message: 'Network error. Please try again or contact us directly at developer@sharedoxygen.com' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    setLoading(true);
    setFormStatus({ submitted: false, success: false, message: '' });

    // Execute invisible reCAPTCHA (will auto-submit via handleCaptchaChange)
    if (!captchaValue) {
      executeRecaptcha();
      
      // Safety timeout: if reCAPTCHA doesn't respond in 10 seconds, show error
      setTimeout(() => {
        if (loading && !captchaValue) {
          console.error('reCAPTCHA timeout');
          setFormStatus({
            submitted: true,
            success: false,
            message: 'Security verification timed out. Please try again.'
          });
          setLoading(false);
        }
      }, 10000);
      return;
    }

    // If captchaValue already exists, submit directly
    await submitForm(captchaValue);
  };

  return (
    <div className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}>
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className={`text-4xl md:text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
            Start The Conversation
          </h1>
          <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
            Share the mandate, data landscape, and decision timeline. We’ll recommend the right first move.
          </p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="container mx-auto px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Narrative Content */}
            <div className={`${theme === 'dark' ? 'bg-gray-800/80' : 'bg-white'} p-8 rounded-lg shadow-md h-fit`}>
              <ul className="space-y-6 list-none">
                <li className="flex gap-4">
                  <div className={`text-blue-500 flex-shrink-0 mt-1 hidden md:block`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-lg block mb-1 md:text-base md:inline md:mr-1 md:mb-0">Ship Agentic AI With Control</span>
                    <span>—strategy, architecture, guardrails, and evaluation matched to your workflows.</span>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className={`text-blue-500 flex-shrink-0 mt-1 hidden md:block`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-lg block mb-1 md:text-base md:inline md:mr-1 md:mb-0">Make Data Dependable</span>
                    <span>with governance and architecture that make AI reliable and observable.</span>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className={`text-blue-500 flex-shrink-0 mt-1 hidden md:block`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-lg block mb-1 md:text-base md:inline md:mr-1 md:mb-0">Start With Focused Pilots</span>
                    <span>that test value and de-risk production rollout.</span>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className={`text-blue-500 flex-shrink-0 mt-1 hidden md:block`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium text-lg block mb-1 md:text-base md:inline md:mr-1 md:mb-0">Measure What Matters</span>
                    <span>with metrics that connect AI performance to enterprise outcomes.</span>
                  </div>
                </li>
              </ul>
              <p className={`mt-8 italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Brief us on the mandate. We’ll return the path to accountable execution.
              </p>
            </div>

            {/* Right Column - Contact Form */}
            <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} p-8 rounded-lg shadow-md`}>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                Send a Message
              </h2>

              {formStatus.submitted && formStatus.success ? (
                <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-800'} mb-6`}>
                  <p>{formStatus.message}</p>
                </div>
              ) : (
                <form
                  name="contact"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {formStatus.submitted && !formStatus.success && (
                    <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-red-800 text-red-100' : 'bg-red-100 text-red-800'} mb-6`}>
                      <p>{formStatus.message}</p>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="name"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="your.email@company.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="subject"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="What mandate should we review?"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="Describe the mandate, current constraints, and desired outcome."
                    ></textarea>
                  </div>
                  {/* Invisible reCAPTCHA */}
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6Lcm3PsrAAAAAItbrJ-PEyr9n2MlIqi5JCDxKNqf"}
                    onChange={handleCaptchaChange}
                    size="invisible"
                    theme={theme === 'dark' ? 'dark' : 'light'}
                  />
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`inline-flex items-center justify-center w-full px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white ${loading
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        } transition-colors`}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                    <p className={`text-xs text-center mt-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      We respond within 1-2 business days.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
